import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWalletAuth } from '../../context/WalletAuthContext';
import { superAdminAPI } from '../../lib/walletApi';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Building2, Users, Wallet, ArrowLeftRight, TrendingUp, ShieldCheck, ScrollText, Loader2, Wifi, WifiOff, Server, Globe, Clock, CheckCircle, AlertTriangle, ArrowRight, Settings, Save, ExternalLink, GraduationCap } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import walletApi from '../../lib/walletApi';
import { toast } from 'sonner';

const COLORS = ['#EC4899', '#8B5CF6', '#3B82F6', '#10B981'];

export default function SuperAdminDashboard() {
  const { user } = useWalletAuth();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [sysStatus, setSysStatus] = useState(null);
  const [approvals, setApprovals] = useState(null);
  const [txHealth, setTxHealth] = useState(null);
  const [webhookFeed, setWebhookFeed] = useState(null);
  const [loading, setLoading] = useState(true);
  const [configStatus, setConfigStatus] = useState(null);
  const [quickProdUrl, setQuickProdUrl] = useState('');
  const [quickStagingUrl, setQuickStagingUrl] = useState('');
  const [savingConfig, setSavingConfig] = useState(false);
  const [configSaved, setConfigSaved] = useState(false);
  const [systemHealth, setSystemHealth] = useState(null);

  const loadApprovals = async () => {
    try { setApprovals(await superAdminAPI.getApprovalDashboard()); } catch {}
  };
  const loadTxHealth = async () => {
    try { setTxHealth(await superAdminAPI.getTransactionHealth()); } catch {}
  };
  const loadWebhookFeed = async () => {
    try { setWebhookFeed(await superAdminAPI.getWebhookFeed({ limit: '5' })); } catch {}
  };
  const loadConfigStatus = async () => {
    try {
      const cs = await superAdminAPI.getConfigStatus();
      setConfigStatus(cs);
      setQuickProdUrl(cs.production_url || '');
      setQuickStagingUrl(cs.staging_url || '');
    } catch {}
  };
  const saveQuickConfig = async () => {
    setSavingConfig(true);
    setConfigSaved(false);
    try {
      const result = await superAdminAPI.updateSystemConfig([
        { key: 'ASSETS_MFB_BASE_URL_PRODUCTION', value: quickProdUrl },
        { key: 'ASSETS_MFB_BASE_URL_STAGING', value: quickStagingUrl },
      ]);
      toast.success('Configuration saved! Active URL: ' + result.active_url);
      setConfigSaved(true);
      await loadConfigStatus();
      setTimeout(() => setConfigSaved(false), 5000);
    } catch (err) {
      toast.error('Failed to save configuration');
    } finally {
      setSavingConfig(false);
    }
  };

  const loadSystemStatus = async () => {
    try {
      const status = await superAdminAPI.getSystemStatus();
      setSysStatus(status);
    } catch {}
  };

  useEffect(() => {
    const load = async () => {
      try {
        const [data, status] = await Promise.all([
          walletApi.get('/super-admin/analytics').then(r => r.data).catch(() => null),
          superAdminAPI.getSystemStatus().catch(() => null),
        ]);
        setAnalytics(data);
        setSysStatus(status);
        loadApprovals();
        loadTxHealth();
        loadWebhookFeed();
        loadConfigStatus();
        // Load system health
        try { setSystemHealth(await walletApi.get('/super-admin/reconciliation/dashboard').then(r => r.data)); } catch {}
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
    // Auto-refresh system status every 60 seconds
    const interval = setInterval(loadSystemStatus, 60000);
    return () => clearInterval(interval);
  }, []);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-pink-400" /></div>;

  const userPie = analytics ? [
    { name: 'Super Admins', value: analytics.users.super_admins },
    { name: 'Company Admins', value: analytics.users.company_admins },
    { name: 'Employees', value: analytics.users.employees },
  ].filter(d => d.value > 0) : [];

  const stats = [
    { label: 'Companies', value: analytics?.companies || 0, icon: Building2, color: 'bg-pink-50 text-pink-600' },
    { label: 'Total Users', value: analytics?.users?.total || 0, icon: Users, color: 'bg-violet-50 text-violet-600' },
    { label: 'Wallets', value: analytics?.wallets?.count || 0, icon: Wallet, color: 'bg-blue-50 text-blue-600' },
    { label: 'Transactions', value: analytics?.transactions?.count || 0, icon: ArrowLeftRight, color: 'bg-emerald-50 text-emerald-600' },
  ];

  const bankingConnected = sysStatus?.banking?.connected;

  return (
    <div className="space-y-6" data-testid="sa-dashboard">
      <div>
        <p className="text-sm text-gray-400">{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
        <h1 className="text-2xl font-bold text-gray-900">Good {new Date().getHours() < 12 ? 'morning' : 'afternoon'}, {user?.first_name}.</h1>
      </div>

      {/* System Status Bar */}
      <Card className="border-gray-200 shadow-sm" data-testid="system-status-card">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-6">
            {/* Banking Connection */}
            <div className="flex items-center gap-3">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${bankingConnected ? 'bg-emerald-50' : 'bg-red-50'}`}>
                {bankingConnected ? <Wifi className="w-4.5 h-4.5 text-emerald-500" /> : <WifiOff className="w-4.5 h-4.5 text-red-500" />}
              </div>
              <div>
                <p className="text-xs text-gray-500 font-medium">Banking Connection</p>
                <div className="flex items-center gap-1.5">
                  <div className={`w-2 h-2 rounded-full ${bankingConnected ? 'bg-emerald-400 animate-pulse' : 'bg-red-400'}`} />
                  <span className={`text-sm font-semibold ${bankingConnected ? 'text-emerald-600' : 'text-red-600'}`} data-testid="banking-status">
                    {bankingConnected ? 'Assets MFB Connected' : 'Assets MFB Disconnected'}
                  </span>
                </div>
              </div>
            </div>

            <div className="w-px h-8 bg-gray-200 hidden sm:block" />

            {/* Environment */}
            <div className="flex items-center gap-3">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${sysStatus?.environment === 'PRODUCTION' ? 'bg-emerald-50' : 'bg-amber-50'}`}>
                <Globe className={`w-4.5 h-4.5 ${sysStatus?.environment === 'PRODUCTION' ? 'text-emerald-500' : 'text-amber-500'}`} />
              </div>
              <div>
                <p className="text-xs text-gray-500 font-medium">Environment</p>
                <span className={`text-sm font-semibold ${sysStatus?.environment === 'PRODUCTION' ? 'text-emerald-600' : 'text-amber-600'}`} data-testid="dashboard-environment">
                  {sysStatus?.environment || '...'}
                </span>
              </div>
            </div>

            <div className="w-px h-8 bg-gray-200 hidden sm:block" />

            {/* Active URL */}
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
                <ExternalLink className="w-4.5 h-4.5 text-blue-500" />
              </div>
              <div>
                <p className="text-xs text-gray-500 font-medium">MFB URL</p>
                <span className="text-xs font-mono font-semibold text-gray-900" data-testid="dashboard-active-url">
                  {sysStatus?.active_url ? sysStatus.active_url.replace('https://', '') : '...'}
                </span>
              </div>
            </div>

            <div className="w-px h-8 bg-gray-200 hidden sm:block" />

            {/* Proxy Status */}
            <div className="flex items-center gap-3">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${sysStatus?.proxy_active ? 'bg-emerald-50' : 'bg-red-50'}`}>
                <ShieldCheck className={`w-4.5 h-4.5 ${sysStatus?.proxy_active ? 'text-emerald-500' : 'text-red-500'}`} />
              </div>
              <div>
                <p className="text-xs text-gray-500 font-medium">Proxy</p>
                <span className={`text-sm font-semibold ${sysStatus?.proxy_active ? 'text-emerald-600' : 'text-red-600'}`} data-testid="dashboard-proxy-status">
                  {sysStatus?.proxy_active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>

            <div className="w-px h-8 bg-gray-200 hidden sm:block" />

            {/* Server IP */}
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
                <Server className="w-4.5 h-4.5 text-blue-500" />
              </div>
              <div>
                <p className="text-xs text-gray-500 font-medium">Outbound IP</p>
                <span className="text-sm font-mono font-semibold text-gray-900" data-testid="server-ip">
                  {sysStatus?.server_ip || '...'}
                </span>
              </div>
            </div>

            {sysStatus?.banking?.balance !== null && sysStatus?.banking?.balance !== undefined && (
              <>
                <div className="w-px h-8 bg-gray-200 hidden sm:block" />
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center">
                    <Wallet className="w-4.5 h-4.5 text-emerald-500" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 font-medium">MFB Balance</p>
                    <span className="text-sm font-semibold text-gray-900" data-testid="mfb-balance">
                      NGN {Number(sysStatus.banking.balance).toLocaleString()}
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map(s => (
          <Card key={s.label} className="border-gray-200 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs text-gray-500 font-medium">{s.label}</span>
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${s.color}`}>
                  <s.icon className="w-4 h-4" />
                </div>
              </div>
              <p className="text-2xl font-bold text-gray-900" data-testid={`stat-${s.label.toLowerCase().replace(/\s/g,'-')}`}>{s.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* User Distribution */}
        <Card className="border-gray-200 shadow-sm">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-gray-700">User Distribution</CardTitle></CardHeader>
          <CardContent>
            <div className="h-48">
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={userPie} cx="50%" cy="50%" innerRadius={50} outerRadius={75} paddingAngle={3} dataKey="value">
                    {userPie.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-center gap-4 mt-2">
              {userPie.map((d, i) => (
                <div key={d.name} className="flex items-center gap-1.5 text-xs text-gray-500">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS[i] }} />
                  {d.name}: {d.value}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Platform Overview */}
        <Card className="border-gray-200 shadow-sm lg:col-span-2">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-gray-700">Platform Overview</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Wallet className="w-4 h-4 text-pink-500" />
                  <span className="text-xs text-gray-500 font-medium">Total Balance</span>
                </div>
                <p className="text-xl font-bold text-gray-900">NGN {Number(analytics?.wallets?.total_balance || 0).toLocaleString()}</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-4 h-4 text-emerald-500" />
                  <span className="text-xs text-gray-500 font-medium">Transaction Volume</span>
                </div>
                <p className="text-xl font-bold text-gray-900">NGN {Number(analytics?.transactions?.volume || 0).toLocaleString()}</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <ShieldCheck className="w-4 h-4 text-violet-500" />
                  <span className="text-xs text-gray-500 font-medium">Super Admins</span>
                </div>
                <p className="text-xl font-bold text-gray-900">{analytics?.users?.super_admins || 0}</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <ScrollText className="w-4 h-4 text-blue-500" />
                  <span className="text-xs text-gray-500 font-medium">Audit Logs</span>
                </div>
                <p className="text-xl font-bold text-gray-900">{analytics?.audit_logs || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pending Approvals Widget */}
        <Card className="border-gray-200 shadow-sm lg:col-span-3" data-testid="pending-approvals-widget">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CardTitle className="text-sm font-semibold text-gray-700">Pending Approvals</CardTitle>
                {approvals?.total_pending > 0 && (
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${approvals?.overdue_count > 0 ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-600'}`}>
                    {approvals.total_pending}
                  </span>
                )}
              </div>
              <Button variant="ghost" size="sm" className="h-7 text-xs text-pink-600" data-testid="view-all-approvals"
                onClick={() => navigate('/super-admin/approvals')}>
                View all <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {!approvals || approvals.total_pending === 0 ? (
              <div className="text-center py-6">
                <CheckCircle className="w-8 h-8 text-emerald-300 mx-auto mb-2" />
                <p className="text-xs text-gray-400">No payrolls pending approval</p>
              </div>
            ) : (
              <div className="space-y-2">
                {approvals.items?.map(p => (
                  <div key={p.id} className="flex items-center justify-between p-3 rounded-lg bg-gray-50 border border-gray-100" data-testid={`dash-approval-${p.id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{p.company_name}</p>
                      <p className="text-xs text-gray-500">{p.period_label} &middot; {p.employee_count} recipients</p>
                    </div>
                    <div className="text-right shrink-0 mx-3">
                      <p className="text-sm font-bold text-gray-900">NGN {Number(p.total_net).toLocaleString()}</p>
                      <span className={`text-[10px] font-medium ${p.is_overdue ? 'text-red-500' : 'text-gray-400'}`}>
                        {Math.floor(p.waiting_hours)}h {p.is_overdue && <AlertTriangle className="w-3 h-3 inline" />}
                      </span>
                    </div>
                    <Button size="sm" className="h-7 text-xs bg-emerald-500 hover:bg-emerald-600 text-white ml-2"
                      data-testid={`dash-approve-${p.id}`}
                      onClick={async () => {
                        try {
                          await superAdminAPI.approvePayroll(p.id);
                          toast.success(`Approved: ${p.company_name}`);
                          loadApprovals();
                        } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                      }}>
                      <CheckCircle className="w-3 h-3 mr-1" /> Approve
                    </Button>
                  </div>
                ))}
                {approvals.total_pending > 5 && (
                  <p className="text-[10px] text-center text-gray-400">+{approvals.total_pending - 5} more pending</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Transaction Health Widget */}
        <Card className="border-gray-200 shadow-sm lg:col-span-2" data-testid="transaction-health-widget">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CardTitle className="text-sm font-semibold text-gray-700">Transaction Health</CardTitle>
                {txHealth?.unresolved_count > 0 && (
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-600" data-testid="error-badge">
                    {txHealth.unresolved_count}
                  </span>
                )}
              </div>
              <Button variant="ghost" size="sm" className="h-7 text-xs text-pink-600" data-testid="view-all-errors"
                onClick={() => navigate('/super-admin/transaction-errors')}>
                View errors <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {txHealth ? (
              <div className="space-y-3">
                <div className="grid grid-cols-4 gap-2">
                  <div className="text-center p-2 rounded-lg bg-gray-50">
                    <p className="text-lg font-bold text-gray-900">{txHealth.total_today}</p>
                    <p className="text-[10px] text-gray-500">Today</p>
                  </div>
                  <div className="text-center p-2 rounded-lg bg-emerald-50">
                    <p className="text-lg font-bold text-emerald-600">{txHealth.success_today}</p>
                    <p className="text-[10px] text-gray-500">Success</p>
                  </div>
                  <div className="text-center p-2 rounded-lg bg-red-50">
                    <p className="text-lg font-bold text-red-600">{txHealth.failed_today}</p>
                    <p className="text-[10px] text-gray-500">Failed</p>
                  </div>
                  <div className="text-center p-2 rounded-lg bg-blue-50">
                    <p className="text-lg font-bold text-blue-600">{txHealth.success_rate}%</p>
                    <p className="text-[10px] text-gray-500">Rate</p>
                  </div>
                </div>
                {txHealth.recent_failed?.length > 0 && (
                  <div className="space-y-1.5">
                    {txHealth.recent_failed.map(t => (
                      <div key={t.id} className="flex items-center justify-between p-2 rounded-lg bg-red-50/50 border border-red-100 text-xs">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">{t.company_name}</p>
                          <p className="text-red-500 truncate">{t.failure_reason}</p>
                        </div>
                        <p className="font-bold text-gray-900 shrink-0 ml-2">NGN {Number(t.amount).toLocaleString()}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-xs text-gray-400 text-center py-4">Loading...</p>
            )}
          </CardContent>
        </Card>

        {/* Quick Settings Widget */}
        <Card className="border-gray-200 shadow-sm lg:col-span-1" data-testid="quick-settings-widget">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Settings className="w-4 h-4 text-gray-500" />
                <CardTitle className="text-sm font-semibold text-gray-700">Quick Settings</CardTitle>
              </div>
              {configStatus && (
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${configStatus.environment === 'PRODUCTION' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}
                  data-testid="env-badge">
                  {configStatus.environment}
                </span>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {configStatus ? (
              <div className="space-y-3">
                {/* Active URL Display */}
                <div className="p-2.5 rounded-lg bg-gray-50 border border-gray-100">
                  <p className="text-[10px] text-gray-500 font-medium mb-1">CURRENTLY ACTIVE URL</p>
                  <p className="text-xs font-mono font-semibold text-gray-900 break-all" data-testid="active-url">{configStatus.active_url}</p>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${configStatus.url_source === 'DATABASE' ? 'bg-blue-50 text-blue-600' : configStatus.url_source === 'ENV_VAR' ? 'bg-purple-50 text-purple-600' : 'bg-gray-100 text-gray-600'}`}
                      data-testid="url-source">
                      {configStatus.url_source}
                    </span>
                    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${configStatus.ip_matches_fixie ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'}`}>
                      IP: {configStatus.outbound_ip}
                    </span>
                  </div>
                </div>

                {/* Editable Fields */}
                <div className="space-y-2">
                  <div>
                    <label className="text-[10px] font-medium text-gray-500">Production URL</label>
                    <input type="text" value={quickProdUrl} onChange={e => setQuickProdUrl(e.target.value)}
                      className="w-full mt-0.5 px-2.5 py-1.5 text-xs font-mono border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-pink-300 focus:border-pink-300"
                      data-testid="prod-url-input" />
                  </div>
                  <div>
                    <label className="text-[10px] font-medium text-gray-500">Staging URL</label>
                    <input type="text" value={quickStagingUrl} onChange={e => setQuickStagingUrl(e.target.value)}
                      className="w-full mt-0.5 px-2.5 py-1.5 text-xs font-mono border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-pink-300 focus:border-pink-300"
                      data-testid="staging-url-input" />
                  </div>
                </div>

                {/* Save Button */}
                <Button size="sm" className="w-full h-8 text-xs bg-pink-500 hover:bg-pink-600 text-white"
                  onClick={saveQuickConfig} disabled={savingConfig}
                  data-testid="save-config-btn">
                  {savingConfig ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
                  {savingConfig ? 'Saving...' : 'Save & Apply'}
                </Button>

                {/* Success Confirmation */}
                {configSaved && (
                  <div className="flex items-center gap-1.5 p-2 rounded-lg bg-emerald-50 border border-emerald-200" data-testid="config-saved-msg">
                    <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
                    <p className="text-xs text-emerald-700 font-medium">Configuration saved. Active URL updated.</p>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-xs text-gray-400 text-center py-4">Loading config...</p>
            )}
          </CardContent>
        </Card>

        {/* System Health Widget */}
        <Card className="border-gray-200 shadow-sm lg:col-span-3" data-testid="system-health-widget">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-blue-500" />
                <CardTitle className="text-sm font-semibold text-gray-700">System Health</CardTitle>
              </div>
              {systemHealth?.ledger_integrity && (
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${systemHealth.ledger_integrity === 'PASS' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}
                  data-testid="ledger-integrity-badge">
                  Ledger: {systemHealth.ledger_integrity}
                </span>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {systemHealth ? (
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                <div className={`text-center p-3 rounded-lg border ${systemHealth.processing_transactions_stale_count > 0 ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-100'}`}
                  data-testid="stale-txns-metric">
                  <p className={`text-lg font-bold ${systemHealth.processing_transactions_stale_count > 0 ? 'text-amber-600' : 'text-gray-900'}`}>
                    {systemHealth.processing_transactions_stale_count}
                  </p>
                  <p className="text-[10px] text-gray-500">Processing &gt;30m</p>
                </div>
                <div className="text-center p-3 rounded-lg bg-emerald-50 border border-emerald-100" data-testid="tsq-resolved-metric">
                  <p className="text-lg font-bold text-emerald-600">{systemHealth.tsq_auto_resolved_today}</p>
                  <p className="text-[10px] text-gray-500">TSQ Resolved</p>
                </div>
                <div className="text-center p-3 rounded-lg bg-blue-50 border border-blue-100" data-testid="idem-blocks-metric">
                  <p className="text-lg font-bold text-blue-600">{systemHealth.idempotency_blocks_today}</p>
                  <p className="text-[10px] text-gray-500">Idem. Blocks</p>
                </div>
                <div className={`text-center p-3 rounded-lg border ${systemHealth.ledger_integrity === 'FAIL' ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-100'}`}
                  data-testid="ledger-metric">
                  <p className={`text-lg font-bold ${systemHealth.ledger_integrity === 'FAIL' ? 'text-red-600' : 'text-emerald-600'}`}>
                    {systemHealth.ledger_integrity}
                  </p>
                  <p className="text-[10px] text-gray-500">Ledger</p>
                </div>
                <div className="text-center p-3 rounded-lg bg-gray-50 border border-gray-100" data-testid="manual-review-metric">
                  <p className="text-lg font-bold text-gray-900">{systemHealth.manual_review_queue_count}</p>
                  <p className="text-[10px] text-gray-500">Manual Queue</p>
                </div>
              </div>
            ) : (
              <p className="text-xs text-gray-400 text-center py-4">Loading health data...</p>
            )}
            {systemHealth?.last_run_at && (
              <p className="text-[10px] text-gray-400 mt-2" data-testid="last-recon-time">
                Last reconciliation: {new Date(systemHealth.last_run_at).toLocaleString()}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Employee Accounts Quick Access */}
        <Card className="border-gray-200 shadow-sm lg:col-span-3" data-testid="student-accounts-widget">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <GraduationCap className="w-4 h-4 text-pink-500" />
                <CardTitle className="text-sm font-semibold text-gray-700">Employee Accounts</CardTitle>
              </div>
              <Button variant="ghost" size="sm" className="h-7 text-xs text-pink-600" data-testid="manage-students-btn"
                onClick={() => navigate('/super-admin/student-accounts')}>
                Manage <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between p-3 rounded-lg bg-pink-50/50 border border-pink-100">
              <div>
                <p className="text-sm font-medium text-gray-900">Virtual Account Management</p>
                <p className="text-xs text-gray-500 mt-0.5">Create, manage, and export student virtual accounts</p>
              </div>
              <Button size="sm" className="bg-pink-500 hover:bg-pink-600 text-white" data-testid="go-to-students-btn"
                onClick={() => navigate('/super-admin/student-accounts')}>
                <GraduationCap className="w-3.5 h-3.5 mr-1" />Open
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Webhook Feed Widget */}
        <Card className="border-gray-200 shadow-sm lg:col-span-3" data-testid="webhook-feed-widget">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold text-gray-700">Recent Webhooks</CardTitle>
              <Button variant="ghost" size="sm" className="h-7 text-xs text-pink-600"
                onClick={() => navigate('/super-admin/virtual-accounts')}>
                Full feed <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {webhookFeed?.events?.length > 0 ? (
              <div className="space-y-1.5">
                {webhookFeed.events.map(e => (
                  <div key={e.id} className="flex items-center justify-between p-2.5 rounded-lg bg-gray-50 border border-gray-100 text-xs">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{e.company_name || 'Unknown'}</p>
                      <p className="text-gray-500 truncate">{e.sender_name} &middot; {e.narration}</p>
                    </div>
                    <div className="text-right shrink-0 ml-2">
                      {e.amount && <p className="font-bold text-emerald-600">+NGN {Number(e.amount).toLocaleString()}</p>}
                      <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${e.status === 'processed' ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'}`}>
                        {e.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-gray-400 text-center py-4">No webhook events yet</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
